import {ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Event as RouterEvent, NavigationStart, Router} from '@angular/router';
import {distinctUntilChanged, Subscription} from 'rxjs';

import {AuthService} from './services/auth/auth.service';
import {UserAuthInfo} from './models/user-auth-info';
import {WebSocketService} from './services/websocket.service';
import {ChatProgressIntegrationService} from './services/websocket/chat-progress-integration.service';
import {WebSocketConnectionService} from './services/websocket/websocket-connection.service';
import {TelemetryService} from './services/telemetry/telemetry.service';
import {MobilePerformanceService} from './services/mobile-performance.service';
import {filter} from 'rxjs/operators';
import {CookieDebugService} from './services/auth/cookie-debug.service';
import {ConnectionOrchestrator, DisconnectReason} from './services/connection/connection-manager.service';
import {ConsoleLogsCaptureService} from './services/console-logs-capture.service';
import {MetricsConsoleService} from './services/chat/metrics-console.service';
import {ServiceOrchestrator} from './services/deduplication/service-orchestrator.service';
import {PerformanceMeasurement} from './services/deduplication/performance-measurement.service';
import {PerformanceTrackerService} from './services/performance-tracker.service';
import {ChatService} from './services/chat/chat.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'finderwise-ui';

  /** Exposed to the template (null ? logged-out). */
  public userAuthInfoReference: UserAuthInfo | null = null;

  /** Prevents showing login screen during auth processing */
  public isProcessingAuth = false;

  private userAuthInfoSub!: Subscription;
  private webSocketInitialized = false;

  constructor(
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private changeDetector: ChangeDetectorRef,
    private webSocketService: WebSocketService,
    private chatProgressIntegration: ChatProgressIntegrationService,
    private webSocketConnectionService: WebSocketConnectionService,
    private telemetryService: TelemetryService,
    private mobilePerformance: MobilePerformanceService,
    private cookieDebug: CookieDebugService,
    private connectionManager: ConnectionOrchestrator,
    private consoleLogsCapture: ConsoleLogsCaptureService,
    private metricsConsole: MetricsConsoleService,
    private serviceOrchestrator: ServiceOrchestrator,
    private performanceMeasurement: PerformanceMeasurement,
    private performanceTracker: PerformanceTrackerService,
    // FIN-0079: Inject ChatService to register initializers at app startup (before first WebSocket connection)
    // This ensures ChatCommunicationService.initialize() registers subscription initializers with
    // SubscriptionCoordinator BEFORE ConnectionOrchestrator tries to run them. Without this, the callback
    // registration pattern would fail because no initializers would be registered yet.
    private chatService: ChatService
  ) {
    // FIX FIN-0046: Store WebSocket cleanup function for beforeunload handler
    // ngOnDestroy() doesn't fire during page navigation (page.goto()), so we need
    // to make cleanup available to the beforeunload event in AppLifecycleService
    (window as any).__finderwise_cleanup = () => {
      try {
        console.log('[AppComponent] Cleanup called from beforeunload');
        // CRITICAL FIX FIN-0052: Prevent reconnection race condition during page navigation
        //
        // DISCOVERY: Calling webSocketService.disconnect() BEFORE connectionManager.disconnect()
        // creates a race condition:
        // 1. webSocketService.disconnect() closes WebSocket immediately
        // 2. connected$ observable emits false
        // 3. ConnectionManager subscription triggers reconnection (disconnectInProgress still false!)
        // 4. connectionManager.disconnect() sets disconnectInProgress = true (too late!)
        //
        // SOLUTION: Only call connectionManager.disconnect()
        // - Sets disconnectInProgress = true FIRST (prevents reconnection in connected$ subscription)
        // - Stops any existing reconnect timers
        // - THEN calls webSocketConnectionService.disconnect() internally (line 418)
        // - This ensures the flag is set BEFORE connected$ emits false
        //
        // Evidence from CI/CD logs (run 18922189135):
        // [21:11:30.460] Scheduling reconnection attempt 1/20 ← reconnection triggered
        // [21:11:30.468] disconnect() called with reason: NORMAL ← 8ms too late!
        this.connectionManager.disconnect(DisconnectReason.NORMAL);
      } catch (e) {
        console.error('[AppComponent] Error during beforeunload cleanup:', e);
      }
    };
  }

  /* ------------------------------------------------------------------ */
  /*  LIFECYCLE                                                         */

  /* ------------------------------------------------------------------ */

  ngOnInit(): void {
    // ⏱️ PERFORMANCE: Mark Angular bootstrap complete
    this.performanceTracker.mark('angular-bootstrap-complete');
    console.log('[AppComponent] ngOnInit() called at', new Date().toISOString());

    // Set up performance timeline monitoring
    this.setupPerformanceMonitoring();

    // Start capturing console logs ONLY if recording is enabled (opt-in)
    if (this.consoleLogsCapture.isRecordingEnabled()) {
      this.consoleLogsCapture.startCapturing();
    }

    // Initialize all services using ServiceOrchestrator to prevent duplicates
    console.log('[AppComponent] Initializing all services with ServiceOrchestrator...');
    this.performanceMeasurement.measureAsyncOperation('service-initialization', async () => {
      await this.serviceOrchestrator.initializeAllServices();
    }).then(() => {
      console.log('[AppComponent] All services initialized successfully');

      // Subscribe to auth state changes AFTER services are initialized
      // This ensures AuthService.initialize() has loaded auth from cookies before subscription
      this.userAuthInfoSub = this.authService.userAuthInfo$
        .pipe(distinctUntilChanged())
        .subscribe(userAuthInfo => {
          console.log('[AppComponent] Auth state changed:', userAuthInfo ? 'LOGGED IN' : 'LOGGED OUT', userAuthInfo);
          if (userAuthInfo && userAuthInfo.token) {
            /* ----------  LOGIN  ---------- */
            console.log('[AppComponent] User logged in, calling handleLogin()');
            this.handleLogin(userAuthInfo);
          } else if (this.userAuthInfoReference) {
            /* ---------  LOG-OUT  --------  */
            // only if we *used to* be logged-in
            console.log('[AppComponent] User logged out, calling handleLogout()');
            this.handleLogout();
          }
          // else: initial null  -  ignore
        });

      // Now that services are initialized, check auth and pre-warm WebSocket if needed
      // This ensures AuthService.initialize() has run and loaded auth state from cookies
      const authInfo = this.authService.getUserAuthInfo();
      if (authInfo && authInfo.email) {
        console.log('[AppComponent] User authenticated, pre-warming WebSocket connection...');
        const connectionStart = Date.now();
        this.webSocketConnectionService.connect().then(() => {
          const connectionTime = Date.now() - connectionStart;
          console.log('[AppComponent] WebSocket pre-warm completed in', connectionTime, 'ms');
          this.telemetryService.recordMetric('websocket.prewarm.time', connectionTime);
        }).catch(err => {
          console.warn('[AppComponent] WebSocket pre-warm failed:', err);
        });
      } else {
        console.log('[AppComponent] User not authenticated, skipping WebSocket pre-warm');
      }
    }).catch(error => {
      console.error('[AppComponent] Service initialization failed:', error);
    });

    // Expose debug method to global window for production debugging
    (window as any).debugCookies = () => this.debugCookies();
    console.log('[AppComponent] Current URL:', window.location.href);
    console.log('[AppComponent] Query params:', window.location.search);

    // Check auth timing
    const authStartTime = sessionStorage.getItem('authStartTime');
    if (authStartTime) {
      const elapsed = Date.now() - parseInt(authStartTime);
      console.log('[AppComponent] Time since auth started:', elapsed, 'ms');
    }
    
    /* -------------------------------------------------------------
     *  1.  Accept OAuth2 redirects (check for authentication)
     * ----------------------------------------------------------- */
    this.route.queryParams.subscribe(async params => {
      console.log('[AppComponent] Query params received:', params);
      
      // Check for OAuth authentication error
      if (params['authError'] === 'oauth_failed') {
        const reason = params['reason'] || 'unknown';
        console.error('[AppComponent] OAuth authentication failed:', reason);

        // Show user-friendly error message
        let errorMessage = 'Authentication failed. Please try again.';

        if (reason === 'missing_state') {
          errorMessage = 'Authentication session expired. Please try logging in again.';
        } else if (reason === 'missing_code') {
          errorMessage = 'Authentication was not completed. Please try again.';
        }

        // TODO: Replace with proper notification service when available
        alert(errorMessage);

        // Clear error from URL
        this.router.navigate([], {
          queryParams: { authError: null, reason: null },
          queryParamsHandling: 'merge'
        });
      }

      // Legacy error handling (for backwards compatibility)
      if (params['authError'] === 'true') {
        console.error('[AppComponent] Authentication error detected in URL (legacy)');
        const authState = sessionStorage.getItem('authState');
        console.error('[AppComponent] Failed auth state:', authState);
        alert('Authentication failed. Please try again.');
      }
      
      // Check if this is an OAuth redirect
      if (params['authenticated'] === 'true') {
        console.log('[AppComponent] OAuth redirect detected - fetching user info');
        this.isProcessingAuth = true;
        // Fetch user info from the backend (uses HttpOnly cookie)
        try {
          const response = await fetch('/api/auth/userinfo', {
            credentials: 'include' // Important: include cookies
          });
          
          console.log('[AppComponent] User info response:', response.status, response.statusText);
          
          if (response.ok) {
            const userInfo = await response.json();
            console.log('[AppComponent] User info received:', userInfo);
            if (userInfo.authenticated) {
              // Note: We don't have the token anymore (it's in HttpOnly cookie)
              // The auth service needs to be updated to work without exposing the token
              const userAuthInfo: UserAuthInfo = {
                givenName: userInfo.givenName || userInfo.name,
                fullName: userInfo.name, // Store full name for initials
                email: userInfo.email,
                profilePictureUrl: userInfo.picture || '',
                token: 'httponly' // Placeholder since token is in HttpOnly cookie
              };
              this.authService.setUserAuthInfo(userAuthInfo);
              this.stripAuthParamsFromUrl();
              // Auth processing complete
              this.isProcessingAuth = false;
            } else {
              console.error('[AppComponent] User info indicates not authenticated');
              this.isProcessingAuth = false;
            }
          } else {
            console.error('[AppComponent] Failed to fetch user info:', response.status, await response.text());
            this.isProcessingAuth = false;
          }
        } catch (error) {
          console.error('[AppComponent] Failed to fetch user info after OAuth redirect:', error);
          this.isProcessingAuth = false;
        }
      }
      
      // Handle token in URL (from OAuth redirect)
      const token = params['token'];
      console.log('[AppComponent] Token in URL?', token ? 'YES (length: ' + token.length + ')' : 'NO');
      if (token) {
        this.isProcessingAuth = true;
        console.log('[AppComponent] Processing token from URL at', new Date().toISOString());

        // CRITICAL FIX (FIN-0064): Wait for token exchange BEFORE setting auth state
        // The backend sets ws_token cookie during exchange, and WebSocket needs that cookie
        // in the handshake. Setting auth immediately triggers WebSocket connection, which fails
        // without the ws_token cookie. We MUST exchange token first, then set auth state.
        const isLocalDev = window.location.hostname === 'localhost' ||
                          window.location.hostname === '10.0.0.6' ||
                          window.location.hostname.startsWith('192.168.');

        console.log('[AppComponent] Starting token exchange at', new Date().toISOString());
        performance.mark('auth-token-exchange-start');
        try {
          const response = await fetch('/api/auth/exchange-token', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ token }),
            credentials: 'include'
          });

          performance.mark('auth-token-exchange-end');
          performance.measure('auth-token-exchange-duration', 'auth-token-exchange-start', 'auth-token-exchange-end');
          const exchangeMeasure = performance.getEntriesByName('auth-token-exchange-duration')[0];
          console.log(`⏱️ [AUTH-TIMING] Token exchange took ${exchangeMeasure.duration.toFixed(2)}ms`);

          console.log('[AppComponent] Token exchange response:', response.status, response.statusText);
          if (response.ok) {
            const result = await response.json();
            console.log('[AppComponent] Token exchange successful, result:', result);

            // NOW set auth state with full details from exchange (ws_token cookie is set)
            // This triggers WebSocket connection with proper authentication
            console.log('[AppComponent] Setting auth state AFTER token exchange (ws_token cookie available)');
            performance.mark('auth-set-start');
            const authInfo: UserAuthInfo = {
              givenName: params['givenName'] || result.given_name || result.name,
              fullName: result.name || params['givenName'], // Prefer fullName from exchange
              email: params['email'] || result.email,
              profilePictureUrl: params['profilePictureUrl'] || result.picture || '',
              token: isLocalDev ? token : 'secure' // Use actual token in dev, placeholder in prod
            };
            this.authService.setUserAuthInfo(authInfo);
            performance.mark('auth-set-end');
            performance.measure('auth-set-duration', 'auth-set-start', 'auth-set-end');
            const setMeasure = performance.getEntriesByName('auth-set-duration')[0];
            console.log(`⏱️ [AUTH-TIMING] Auth state set took ${setMeasure.duration.toFixed(2)}ms (WebSocket will connect now with ws_token!)`)

            console.log('[AppComponent] Token exchange and auth setup completed at', new Date().toISOString());

            // Verify ws_token cookie is present (for debugging)
            const wsTokenPresent = document.cookie.includes('ws_token=');
            console.log('[AppComponent] ws_token cookie present:', wsTokenPresent ? 'YES' : 'NO');
            if (!wsTokenPresent) {
              console.error('[AppComponent] CRITICAL: ws_token cookie NOT found after exchange! WebSocket auth will fail!');
            }

            this.stripTokenFromUrl(); // Remove token from URL after successful exchange
          } else {
            console.error('[AppComponent] Token exchange failed:', response.status, await response.text());
            this.stripTokenFromUrl();
          }
        } catch (error) {
          console.error('Token exchange error:', error);
          this.stripTokenFromUrl();
        }

        // NOW mark auth processing complete (after token exchange completes)
        // This ensures WebSocket has ws_token cookie available in handshake
        this.isProcessingAuth = false;
      }
    });

    /* -------------------------------------------------------------
    *  2.  Handle navigation events to preserve connections
    * ----------------------------------------------------------- */
    this.router.events.pipe(
      filter((event: RouterEvent): event is NavigationStart => event instanceof NavigationStart)
    ).subscribe((event: NavigationStart) => {
      // When navigating within the app, preserve the WebSocket connection
      if (this.userAuthInfoReference?.token && this.webSocketInitialized) {
        this.webSocketConnectionService.preserveConnectionOnRefresh();
      }
    });

    /* -------------------------------------------------------------
     *  4.  Try to restore connection after refresh
     * ----------------------------------------------------------- */
    if (this.userAuthInfoReference?.token) {
      // Try to restore the previous connection details
      const restored = this.webSocketConnectionService.tryRestoreConnection();
      if (restored) {
        this.handleLogin(this.userAuthInfoReference);
      }
    }

    // Track app start milestone
    this.mobilePerformance.trackMilestone('app_start');
    // Track first paint web vital
    if ('performance' in window && 'getEntriesByType' in performance) {
      const paintEntries = performance.getEntriesByType('paint');
      paintEntries.forEach(entry => {
        if (entry.name === 'first-paint' || entry.name === 'first-contentful-paint') {
          this.mobilePerformance.trackWebVital(entry.name, entry.startTime);
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.userAuthInfoSub?.unsubscribe();
    try {
      this.webSocketService.disconnect();
    } catch (e) {
      console.error('Error disconnecting WebSocket on destroy:', e);
    }

    // Stop console logs capture
    this.consoleLogsCapture.stopCapturing();
  }

  /* ------------------------------------------------------------------ */
  /*  HELPERS                                                           */

  /* ------------------------------------------------------------------ */

  /** Handle the very first login (or a re-login after logout). */
  private handleLogin(userAuthInfo: UserAuthInfo): void {
    console.log('[AppComponent] handleLogin() called at', new Date().toISOString());
    this.userAuthInfoReference = userAuthInfo;

    // ⏱️ PERFORMANCE: Mark auth complete
    this.performanceTracker.mark('auth-complete');

    console.log('[AppComponent] Triggering change detection...');
    this.changeDetector.detectChanges();

    if (!this.webSocketInitialized) {
      console.log('[AppComponent] Initializing WebSocket services...');
      this.webSocketInitialized = true;
      this.webSocketService.initialize().catch(error => {
        console.error('[AppComponent] Failed to initialize WebSocket service:', error);
      });
      this.chatProgressIntegration.initialize();
      console.log('[AppComponent] WebSocket services initialized');
    } else {
      console.log('[AppComponent] WebSocket already initialized');
    }

    // Log auth completion time
    const authStartTime = sessionStorage.getItem('authStartTime');
    if (authStartTime) {
      const totalTime = Date.now() - parseInt(authStartTime);
      console.log('[AppComponent] ✅ Authentication completed in', totalTime, 'ms');
      sessionStorage.removeItem('authStartTime');
    }
  }

  /** Handle a real logout (was logged-in  -  now null). */
  private handleLogout(): void {
    this.userAuthInfoReference = null;
    this.webSocketInitialized = false;

    try {
      this.webSocketService.disconnect();
    } catch (e) {
      console.error('Error disconnecting WebSocket on logout:', e);
    }

    /* Send the user to the public page (replaceUrl = no extra history entry). */
    this.router.navigate(['/'], {replaceUrl: true});

    this.changeDetector.detectChanges();
  }

  /** Remove token & profile params from the address bar after successful login. */
  private stripTokenFromUrl(): void {
    this.router.navigate([], {
      queryParams: {
        token: null,
        givenName: null,
        email: null,
        profilePictureUrl: null
      },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }
  
  /** Remove auth params from the address bar after successful OAuth redirect. */
  private stripAuthParamsFromUrl(): void {
    this.router.navigate([], {
      queryParams: {
        authenticated: null,
        givenName: null,
        email: null,
        profilePictureUrl: null
      },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  /**
   * FIX FIN-0052: Removed beforeunload telemetry flush handler
   *
   * REMOVED: The forceFlush() on beforeunload was causing telemetry requests to hang during
   * page navigation, exhausting HTTP/2 connection pool (6-10 concurrent requests max) and
   * blocking config.json loading on the new page → blank screen.
   *
   * NEW APPROACH: TelemetryService now uses pagehide event to cancel pending requests
   * immediately instead of flushing, preventing connection exhaustion.
   *
   * See: TelemetryService.constructor() for pagehide handler
   */
  
  /**
   * Set up comprehensive performance monitoring to identify event loop blocking
   * This captures all performance marks and measures to help debug timing issues
   */
  private setupPerformanceMonitoring(): void {
    // Create a PerformanceObserver to capture all marks and measures
    if (typeof PerformanceObserver !== 'undefined') {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach(entry => {
          if (entry.entryType === 'measure') {
            const duration = entry.duration.toFixed(2);
            const warning = entry.duration > 1000 ? '⚠️ SLOW!' : '';
            console.log(`⏱️ [PERF-MEASURE] ${entry.name}: ${duration}ms ${warning}`);
          } else if (entry.entryType === 'mark') {
            console.log(`📍 [PERF-MARK] ${entry.name} at ${entry.startTime.toFixed(2)}ms`);
          }
        });
      });

      // Observe both marks and measures
      observer.observe({ entryTypes: ['mark', 'measure'] });

      // When WebSocket connects, log complete performance timeline
      const checkForStompConnect = setInterval(() => {
        const stompConnectMeasure = performance.getEntriesByName('ws-open-to-stomp-connect');
        if (stompConnectMeasure.length > 0) {
          clearInterval(checkForStompConnect);

          // Log complete timeline
          console.log('\n═══════════════════════════════════════════');
          console.log('🔍 COMPLETE PERFORMANCE TIMELINE');
          console.log('═══════════════════════════════════════════');

          const allMarks = performance.getEntriesByType('mark').sort((a, b) => a.startTime - b.startTime);
          const allMeasures = performance.getEntriesByType('measure').sort((a, b) => a.startTime - b.startTime);

          console.log('\n📍 Performance Marks (chronological):');
          allMarks.forEach(mark => {
            console.log(`  ${mark.startTime.toFixed(2)}ms - ${mark.name}`);
          });

          console.log('\n⏱️ Performance Measures:');
          allMeasures.forEach(measure => {
            const slow = measure.duration > 1000 ? ' ⚠️ BOTTLENECK!' : '';
            console.log(`  ${measure.name}: ${measure.duration.toFixed(2)}ms${slow}`);
          });

          // Identify the longest blocking operation
          const longestMeasure = allMeasures.reduce((longest, current) =>
            current.duration > longest.duration ? current : longest
          , allMeasures[0]);

          console.log('\n🎯 LIKELY CULPRIT:');
          console.log(`  ${longestMeasure.name}: ${longestMeasure.duration.toFixed(2)}ms`);
          console.log('═══════════════════════════════════════════\n');
        }
      }, 100);

      // Safety timeout to clear interval
      setTimeout(() => clearInterval(checkForStompConnect), 30000);
    }
  }

  /**
   * Debug method for testing cookie access in production
   * Can be called from browser console: window.debugCookies()
   */
  debugCookies(): void {
    console.log('[AppComponent] Running cookie debug...');
    this.cookieDebug.debugCookies();
  }
}
